/**
 * Sprint 12+B (02.07.2026) — Pionier-Danke-Seite (Cookie-basiert).
 * Sprint 12+K/L4.2/L4.4/L4.5 — Retry-Chain + Toggle-Box.
 *
 * Sprint 12+L4.6 (15.07.2026) — KOMPLETT-UMBAU: „Magic-Link = Auth".
 *   - Kein Cookie-Check mehr (Chrome-Cross-Origin-Blocker eliminiert).
 *   - Kein `/api/auth/me`-Aufruf, keine Retry-Chain, keine 3-State-Machine.
 *   - Token wird aus URL-Query-Param `?token=xxx` gelesen.
 *   - Backend `GET /api/warteliste/verify?token=xxx` liefert vorname/email
 *     idempotent (verbraucht Token nicht → Reload möglich).
 *   - Toggle-Box („Willst du Pionier-Vorteile erhalten?") ENTFERNT.
 *   - Library-Preview als statische Mental-Trigger-Copy (Jeff Walker /
 *     Russell Brunson: Curiosity + Anticipation + Identity Validation).
 *   - Quick-Check als direkter Link mit `?token=<same-token>&ref=pionier`.
 *   - Kein „Bitte zuerst über die Bestätigungs-Mail einloggen" mehr.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, MessageSquare, BookOpen, Loader2, AlertCircle, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
// Sprint M5.32 (27.07.2026) — Provider-Box mit In-App-Reconfirm-Fallback.
import ProviderConfirmBox from "@/components/ProviderConfirmBox";

const STATE_LOADING = "loading";
const STATE_VALID = "valid";
const STATE_INVALID = "invalid";
const STATE_NO_TOKEN = "no-token";

export default function PionierDanke() {
  const [state, setState] = useState(STATE_LOADING);
  const [vorname, setVorname] = useState("");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [reason, setReason] = useState("");
  // Sprint M5.32 (27.07.2026) — Provider-Box, wenn der User frisch vom
  // Opt-in kommt und nur `?email=` mitbringt (noch keine Bestätigung).
  const [providerHintEmail, setProviderHintEmail] = useState("");
  const { refresh, user: authUser } = useAuth() || {};

  useEffect(() => {
    let cancelled = false;

    const urlParams = new URLSearchParams(window.location.search);
    const rawToken = urlParams.get("token");
    const rawSubKey = urlParams.get("subscriberKey");
    const rawEmail = urlParams.get("email");

    // Sprint 12+M5.17-g (22.07.2026) — Doppelte-Eingabe-Fix Teil B:
    // Wenn der User bereits authentifiziert ist (Cookie da, AuthContext
    // hydratisiert) und KEINE URL-Params vorhanden sind, direkt den
    // Success-Screen rendern statt der Bridge-Eingabe. Verhindert die
    // zweite E-Mail-Eingabe nach dem `/warteliste/one-click`-Redirect.
    if (!rawToken && !rawSubKey && !rawEmail && authUser && authUser.email) {
      setVorname(authUser.first_name || "");
      setEmail(authUser.email);
      setState(STATE_VALID);
      return;
    }

    // Sprint 12+L4.6 → 12+L4.7 (15.07.2026) — Auth-Prio-Reihenfolge:
    //   1) ?token= (unser confirm_token; direkter DB-Lookup)
    //   2) ?subscriberKey= (KT-interne Key-UUID; via KT-API auflösen)
    //   3) ?email= (Fallback, falls Andreas KT-URL mit merge-tag=email rendert)
    let verifyUrl = null;
    if (rawToken && rawToken.trim()) {
      setToken(rawToken.trim());
      verifyUrl = `/warteliste/verify?token=${encodeURIComponent(rawToken.trim())}`;
    } else if (rawSubKey && rawSubKey.trim()) {
      setToken(rawSubKey.trim()); // Für Quick-Check-Link weitergeben
      verifyUrl = `/warteliste/verify?subscriberKey=${encodeURIComponent(rawSubKey.trim())}`;
    } else if (rawEmail && rawEmail.trim()) {
      // Sprint M5.32: Email ohne Token/Subkey ⇒ frischer Opt-in-Redirect.
      // Provider-Box aktivieren, die dem User die richtige Mailbox-Ecke zeigt
      // und optional einen In-App-Reconfirm-Link generiert.
      setProviderHintEmail(rawEmail.trim());
      verifyUrl = `/warteliste/verify?email=${encodeURIComponent(rawEmail.trim())}`;
    }

    if (!verifyUrl) {
      setState(STATE_NO_TOKEN);
      return;
    }

    (async () => {
      try {
        const res = await api.get(verifyUrl);
        if (cancelled) return;
        if (res?.data?.valid) {
          setVorname(res.data.vorname || "");
          setEmail(res.data.email || "");
          setState(STATE_VALID);
          // Sprint 12+M5.17-f (21.07.2026) — KlickTipp-DOI-Token-Loss-Fix:
          // Backend hat bei erfolgreichem Verify bereits einen Auth-Cookie
          // gesetzt (via `_hydrate_pionier_cookie`). Wir triggern jetzt
          // einen refresh() im AuthContext, damit der Quick-Check-Link
          // direkt ohne zweite Auth-Runde funktioniert.
          if (typeof refresh === "function") {
            try {
              await refresh();
            } catch (_e) {
              // non-fatal — Quick-Check-Auth-Endpoint hat eigenen Retry
            }
          }
          // Sprint 12+M5.17-h (22.07.2026) — Meta CAPI Client-Side Dedup:
          // Backend feuert Lead-Event via CAPI und gibt uns `capi_event_id`.
          // Wir feuern denselben Event client-seitig via fbq mit `eventID`
          // Property — Meta dedupliziert die beiden. Zusaetzlich dataLayer-
          // Push fuer optionales GTM-Setup. Beides idempotent-guarded gegen
          // React-18-StrictMode-Doppel-Effects.
          const capiEventId = res.data.capi_event_id || "";
          if (capiEventId && !window.__f4s_lead_tracked) {
            window.__f4s_lead_tracked = true;
            try {
              const emailVal = res.data.email || "";
              const value = 15.0; // wird spaeter mit fokustyp differenziert
              const currency = "EUR";
              if (typeof window.fbq === "function") {
                window.fbq(
                  "track",
                  "Lead",
                  {
                    content_name: "warteliste_doi_confirmed",
                    content_category: "pionier_confirmed",
                    value,
                    currency,
                    predicted_ltv: value * 3.3,
                  },
                  { eventID: capiEventId },
                );
              }
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: "lead",
                event_id: capiEventId,
                value,
                currency,
                email_hash_present: !!emailVal,
              });
            } catch (_e) {
              /* Tracking-Fehler duerfen den User-Flow niemals blockieren */
            }
          }
        } else {
          // Sprint M5.32 (27.07.2026) — Wenn User frisch vom Opt-in kommt
          // (`?email=`), noch nicht bestätigt ist, aber im System existiert:
          // statt Fehler die Provider-Box zeigen (STATE_NO_TOKEN übernimmt
          // das Rendering, weil dort `providerHintEmail` geprüft wird).
          if (rawEmail && rawEmail.trim()) {
            setState(STATE_NO_TOKEN);
          } else {
            setReason(res?.data?.reason || "unknown");
            setState(STATE_INVALID);
          }
        }
      } catch (_err) {
        if (cancelled) return;
        // Bei Netzwerk-Fehler mit `?email=` ebenfalls Provider-Box zeigen —
        // ein Ausfall des Verify-Endpoints darf den User nicht blockieren.
        if (rawEmail && rawEmail.trim()) {
          setState(STATE_NO_TOKEN);
        } else {
          setReason("network_error");
          setState(STATE_INVALID);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Post-Launch-H4 (10.02.2026) — Bewusst leere Deps: dieser Effekt darf
    // NUR bei Mount laufen (Token-Verify via URL-Query). authUser/refresh
    // werden im separaten Safety-Effect (siehe unten) beobachtet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sprint 12+M5.17-g (22.07.2026) — Safety-Effect: AuthContext hydratisiert
  // asynchron (fetch /api/auth/me). Falls PionierDanke frueher rendert als
  // AuthContext geladen ist UND keine URL-Params vorhanden waren, landen
  // wir zunaechst in STATE_NO_TOKEN und wuerden die Bridge zeigen. Sobald
  // der AuthContext dann den User bringt, wechseln wir automatisch auf
  // Success-Screen — verhindert Bridge-Anzeige bei bereits eingeloggten
  // Usern nach spaeter Hydration.
  useEffect(() => {
    if (state === STATE_NO_TOKEN && authUser && authUser.email) {
      setVorname(authUser.first_name || "");
      setEmail(authUser.email);
      setState(STATE_VALID);
    }
  }, [authUser, state]);

  // Sprint 12+M5.17-k (22.07.2026) — Bridge Opt-in -> Cockpit.
  // Wenn User authentifiziert (nach DOI) und QuickCheck noch nicht
  // abgeschlossen (kein primary_fokustyp) -> direkt in den QuickCheck
  // navigieren, damit der Flow ohne Zwischen-Klick durchlaeuft. Wenn
  // fokustyp bereits gesetzt -> ins Cockpit. Die kurze Erfolgs-Anzeige
  // von 1.8s gibt dem User einen "Du bist drin"-Moment; danach fluessige
  // Weiterleitung ohne weiteren Tap.
  const navigate = useNavigate();
  useEffect(() => {
    if (state !== STATE_VALID || !authUser || !authUser.email) return;
    const hasFokustyp = !!(authUser.primary_fokustyp || authUser.fokustyp);
    const target = hasFokustyp ? "/dashboard/pionier" : "/pionier-quick-check";
    const t = setTimeout(() => {
      try {
        navigate(target, { replace: true });
      } catch {
        window.location.href = target;
      }
    }, 1800);
    return () => clearTimeout(t);
  }, [state, authUser, navigate]);

  return (
    <div
      className="min-h-screen bg-slate-50 py-12 sm:py-16 px-4"
      data-testid="pionier-danke-page"
    >
      <div className="max-w-2xl mx-auto">
        {state === STATE_LOADING && <LoadingHero />}
        {state === STATE_INVALID && <InvalidTokenHero reason={reason} />}
        {state === STATE_NO_TOKEN && (
          <>
            {providerHintEmail ? (
              <div className="mt-8" data-testid="pionier-danke-provider-box-slot">
                <ProviderConfirmBox email={providerHintEmail} />
              </div>
            ) : (
              <NoTokenEmailInputHero />
            )}
          </>
        )}
        {state === STATE_VALID && (
          <>
            <SuccessHero vorname={vorname} />
            <div className="space-y-6">
              <SectionListe vorname={vorname} />
              <SectionQuickCheck token={token} />
              <SectionLibraryPreview />
            </div>
            <p
              className="mt-12 text-center text-xs sm:text-sm text-slate-400"
              data-testid="pionier-danke-spam-hint"
            >
              Tipp: Falls du eine Bestätigungs-Mail vermisst, wirf einen Blick
              in deinen Spam-Ordner — manchmal landet sie dort.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function LoadingHero() {
  return (
    <div className="text-center py-16" data-testid="pionier-danke-loading">
      <Loader2 className="w-8 h-8 text-[#4563BB] animate-spin mx-auto mb-4" />
      <p className="text-sm text-slate-500">Einen Moment, wir prüfen deinen Link …</p>
    </div>
  );
}

function InvalidTokenHero({ reason }) {
  const messageMap = {
    token_missing: "In deiner URL fehlt der Bestätigungs-Token. Bitte klick den Link aus deiner Bestätigungs-Mail direkt an.",
    token_not_found: "Wir konnten deinen Link nicht mehr finden. Bitte nutze den Original-Link aus deiner Bestätigungs-Mail.",
    token_expired: "Dein Bestätigungs-Link ist abgelaufen. Bitte trag dich noch einmal auf der Warteliste ein — wir schicken dir einen frischen Link.",
    network_error: "Wir konnten den Server gerade nicht erreichen. Bitte lad die Seite in einem Moment noch einmal.",
    unknown: "Wir konnten deinen Link nicht überprüfen. Bitte kontaktier uns oder trag dich noch einmal auf der Warteliste ein.",
  };
  const msg = messageMap[reason] || messageMap.unknown;
  return (
    <div
      className="bg-white rounded-2xl border border-red-200 p-8 sm:p-10 text-center"
      data-testid="pionier-danke-invalid"
    >
      <div className="inline-flex w-14 h-14 rounded-full bg-red-100 items-center justify-center mb-4">
        <AlertCircle className="w-8 h-8 text-red-600" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-3">
        Link nicht gültig
      </h1>
      <p className="text-slate-600 max-w-lg mx-auto" data-testid="pionier-danke-invalid-text">
        {msg}
      </p>
      <p className="mt-4 text-xs text-slate-400" data-testid="pionier-danke-invalid-code">
        Fehler-Code: <span className="font-mono">{reason || "unknown"}</span>
      </p>
      <a
        href="/warteliste"
        className="mt-6 inline-flex items-center text-sm font-semibold text-[#4563BB] hover:underline"
        data-testid="pionier-danke-invalid-back-link"
      >
        Zurück zur Warteliste →
      </a>
    </div>
  );
}

function NoTokenEmailInputHero() {
  // Sprint 12+M5.17-f (21.07.2026) — Fallback fuer KlickTipp-DOI-Token-Loss.
  //
  // Wenn KT die DOI-Redirect-URL ohne Merge-Params (email/subscriberKey)
  // aufruft, landen wir hier. Statt einen zweiten Magic-Link-Roundtrip
  // zu machen, holen wir uns die Email einmal per Textfeld (der User
  // kommt ja gerade aus SEINER eigenen Bestaetigungs-Mail — kein
  // fremdes Login). Backend `POST /warteliste/confirm-email` setzt in
  // gleicher Response den Auth-Cookie -> refresh() zieht User in
  // AuthContext -> Success-Screen rendert -> "Test starten" geht direkt.
  const { refresh } = useAuth() || {};
  const [inputEmail, setInputEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [vorname, setVorname] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    const em = (inputEmail || "").trim().toLowerCase();
    if (!em || !em.includes("@")) {
      setError("Bitte trag eine gültige E-Mail ein.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/warteliste/confirm-email", { email: em });
      if (res?.data?.success) {
        setVorname(res.data.first_name || "");
        setDone(true);
        if (typeof refresh === "function") {
          try {
            await refresh();
          } catch (_e) {
            // non-fatal
          }
        }
      } else {
        setError("Wir konnten dich nicht zuordnen. Bitte prüf die E-Mail-Adresse.");
      }
    } catch (_err) {
      setError("Verbindung fehlgeschlagen. Bitte in einem Moment noch einmal versuchen.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    // Nach erfolgreichem confirm-email zeigen wir den vollen Success-Flow
    // (SuccessHero + Sections). Der Cookie ist gesetzt, "Test starten"
    // geht direkt in den Quick-Check.
    return (
      <>
        <SuccessHero vorname={vorname} />
        <div className="space-y-6">
          <SectionListe vorname={vorname} />
          <SectionQuickCheck token="" />
          <SectionLibraryPreview />
        </div>
      </>
    );
  }

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 shadow-sm text-center"
      data-testid="pionier-danke-no-token-hero"
    >
      <div className="flex justify-center mb-6">
        <img
          src="/brand/flex4success-wordmark.png"
          alt="Flex4Success"
          className="h-10 sm:h-12 w-auto select-none"
          draggable="false"
        />
      </div>
      <div className="inline-flex w-14 h-14 rounded-full bg-emerald-50 items-center justify-center mb-4">
        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-3">
        Willkommen zurück!
      </h1>
      <p
        className="text-slate-600 max-w-lg mx-auto text-base"
        data-testid="pionier-danke-no-token-copy"
      >
        Du hast deine E-Mail bereits bestätigt — perfekt. Trag sie zur
        Sicherheit noch einmal ein, damit wir deinen PocketCoach direkt für dich
        laden können. Danach geht es ohne weiteren Zwischenschritt weiter.
      </p>
      <form onSubmit={onSubmit} className="mt-6 max-w-md mx-auto">
        <label htmlFor="pionier-danke-email-input" className="sr-only">
          Deine E-Mail-Adresse
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              id="pionier-danke-email-input"
              type="email"
              required
              autoFocus
              autoComplete="email"
              inputMode="email"
              placeholder="deine@e-mail.de"
              value={inputEmail}
              onChange={(e) => setInputEmail(e.target.value)}
              disabled={submitting}
              className="w-full pl-9 pr-3 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#4563BB] focus:border-transparent disabled:opacity-60"
              data-testid="pionier-danke-no-token-email-input"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center px-5 py-3 bg-[#4563BB] text-white font-semibold rounded-lg hover:bg-[#3a54a3] disabled:opacity-60 transition-colors"
            data-testid="pionier-danke-no-token-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Lädt…
              </>
            ) : (
              <>
                PocketCoach laden
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </button>
        </div>
        {error && (
          <p
            className="mt-3 text-sm text-red-600"
            data-testid="pionier-danke-no-token-error"
          >
            {error}
          </p>
        )}
      </form>
      <p className="mt-6 text-xs text-slate-400">
        Kein weiterer Magic-Link nötig — du bleibst hier auf der Seite.
      </p>
    </div>
  );
}

function SuccessHero({ vorname }) {
  // Sprint 12+M5.3 (18.07.2026) — Andreas' Launch-Polish:
  // Flex4Success-Logo prominent + überarbeitete Headline + "Reaktionsmuster"
  // statt "Weltbilder" (alltagsnah, nicht philosophisch).
  const hasName = Boolean(vorname && vorname.trim());
  return (
    <div className="text-center mb-10 sm:mb-14" data-testid="pionier-danke-hero">
      <div className="flex justify-center mb-6">
        <img
          src="/brand/flex4success-wordmark.png"
          alt="Flex4Success"
          className="h-10 sm:h-12 w-auto select-none"
          draggable="false"
          data-testid="pionier-danke-logo"
        />
      </div>
      <h1
        className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900"
        data-testid="pionier-danke-headline"
      >
        {hasName
          ? `Herzlich willkommen, ${vorname}. Du bist ab sofort Flex4Success-Pionier.`
          : "Herzlich willkommen, du bist ab sofort Flex4Success-Pionier."}
      </h1>
      <p
        className="mt-4 text-base sm:text-lg text-slate-600 max-w-xl mx-auto"
        data-testid="pionier-danke-subheadline"
      >
        Du bist einer der Ersten, die sehen, wie vier typische
        Reaktionsmuster zusammenwirken.
      </p>
    </div>
  );
}

function SectionListe({ vorname }) {
  const hasName = Boolean(vorname && vorname.trim());
  // Sprint 12+M5.9-b (19.07.2026) — Andreas' Microcopy-Finalisierung:
  // Header „Du bist dabei." + fette Sub-Headline „Du gehörst zu den
  // Flex-Pionieren." + neuer Sub-Text ohne Redundanz zum Hero.
  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-sm"
      data-testid="pionier-danke-section-was-passiert-jetzt"
    >
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
          Du bist dabei
        </p>
      </div>
      <h2
        className="text-lg sm:text-xl font-bold text-slate-900"
        data-testid="pionier-danke-section-liste-title"
      >
        {hasName
          ? `Du gehörst zu den Flex-Pionieren, ${vorname}.`
          : "Du gehörst zu den Flex-Pionieren."}
      </h2>
      <p className="mt-2 text-sm sm:text-base text-slate-600">
        Du erfährst als Erstes, wenn es spannende Neuigkeiten gibt. Kein
        Newsletter-Rauschen, nur die wichtigen Momente.
      </p>
    </div>
  );
}

function SectionQuickCheck({ token }) {
  // Sprint 12+L4.6 — Direkter Link zum Quick-Check (gleicher Pfad wie
  // die Pionier-Test-Route). Token wird als Query-Param mitgegeben, damit
  // die Ziel-Seite den Nutzer identifizieren kann (Analytics + ggf.
  // Backend-Auth-Setup ohne Cookie-Roundtrip). Die Quick-Check-Route selbst
  // ist Cookie-basiert; sobald Andreas `COOKIE_SAMESITE=none` in Prod
  // gesetzt hat, funktioniert dieser Link ohne zweite Auth-Runde.
  const href = token
    ? `/pionier-quick-check?token=${encodeURIComponent(token)}&ref=pionier`
    : `/pionier-quick-check`;
  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-sm"
      data-testid="pionier-danke-section-quick-check"
    >
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="w-5 h-5 text-[#4563BB]" />
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
          Was du tun kannst
        </p>
      </div>
      <h2 className="text-lg sm:text-xl font-bold text-slate-900">
        Erstelle dein Flex4Success-Profil in 2 Minuten.
      </h2>
      <p className="mt-2 text-sm sm:text-base text-slate-600">
        In 2 Minuten weißt du, welcher Fokustyp du bist. Damit passt dein
        Flexpfad ab Tag 1 zu dir.
      </p>
      <div className="mt-4">
        <a
          href={href}
          className="inline-flex items-center text-sm font-semibold text-[#4563BB] hover:underline"
          data-testid="pionier-danke-quickcheck-link"
        >
          Erstelle dein Flex4Success-Profil in 2 Minuten
          <ArrowRight className="w-4 h-4 ml-1" />
        </a>
      </div>
    </div>
  );
}

function SectionLibraryPreview() {
  // Sprint 12+L4.6 — Library-Preview mit Mental-Trigger-Copy nach
  // Jeff-Walker-PLF (Curiosity, Anticipation, Identity Validation) +
  // Russell-Brunson-Hook-Story-Offer (Hook = "Vier Weltsichten. Vier
  // Stimmen im Kopf." · Story = "die du jetzt schon hast" · Offer =
  // "Zugang zu allen vier Bibliotheken"). Kein CTA-Link (bewusst statisch).
  return (
    <div
      className="bg-white rounded-2xl border border-amber-200 p-6 sm:p-7 shadow-sm"
      style={{ backgroundColor: "#fef7ed" }}
      data-testid="pionier-danke-section-library-preview"
    >
      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="w-5 h-5 text-amber-700" />
        <p className="text-xs font-bold uppercase tracking-widest text-amber-800">
          Was du bekommst
        </p>
      </div>
      <h2 className="text-lg sm:text-xl font-bold text-slate-900">
        Du bist einer der Ersten, die sehen, wie vier typische
        Reaktionsmuster zusammenwirken.
      </h2>
      <div className="mt-3 space-y-3 text-sm sm:text-base text-slate-700 leading-relaxed">
        <p>
          Vier Reaktionsmuster. Vier Wege, Situationen wahrzunehmen und zu
          beantworten — die du jetzt schon hast, aber vielleicht noch nie
          bewusst kombiniert.
        </p>
        <p className="font-semibold text-slate-900">Katze. Eule. Biene. Hund.</p>
        <p>
          Vier Wege, die Welt zu sehen. Vier Wege, mit ihr zu sprechen.
        </p>
        <p>
          Sobald wir starten, bekommst du Zugang zu allen vier Bibliotheken —
          inklusive der Vorab-Module, die wir gerade für dich bauen.
        </p>
        <p className="italic text-amber-900">
          Du bist einer der Ersten, die sehen, wie vier typische
          Reaktionsmuster zusammenwirken. Bald geht es los — du gehörst zu
          den Ersten, die es erfahren.
        </p>
      </div>
    </div>
  );
}
